import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type { ShopifyLinkFilter, ShopifyLinkResult, ShopifyVariant } from "../api/types";
import { BouquetTemplateThumb } from "../components/BouquetTemplateThumb";
import { ItemThumb } from "../components/ItemThumb";
import { ShopifyBarcodeMatchModal } from "../components/shopify/ShopifyBarcodeMatchModal";
import { ShopifyLinkPicker } from "../components/shopify/ShopifyLinkPicker";
import { shopifyVariantLabel } from "../components/shopify/shopifyVariantLabel";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { money } from "../i18n/intl";

function errText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * G-111 S4: the store's products, one card per size, and what each one is in
 * the CRM. Orders only reach the shops for sizes linked here (OD-91/92).
 */
export function ShopifyProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<ShopifyLinkFilter>("unlinked");
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [variants, setVariants] = useState<ShopifyVariant[] | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pickerFor, setPickerFor] = useState<ShopifyVariant | null>(null);
  const [matching, setMatching] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    api
      .listShopifyProducts({ q: appliedQuery || undefined, linked: filter })
      .then((result) => {
        if (cancelled) return;
        setVariants(result.variants);
        setRefreshedAt(result.refreshedAt);
        setError(null);
      })
      .catch((e) => !cancelled && setError(errText(e, "Could not load Shopify products")));
    return () => {
      cancelled = true;
    };
  }, [appliedQuery, filter, reloadTick]);

  // "Link" from a Shopify order opens the picker for that order line's size.
  const deepLinkedVariant = searchParams.get("variant");
  useEffect(() => {
    if (!deepLinkedVariant) return;
    let cancelled = false;
    api
      .getShopifyProduct(deepLinkedVariant)
      .then((variant) => !cancelled && setPickerFor(variant))
      .catch((e) => !cancelled && setError(errText(e, "Could not open that Shopify product")));
    return () => {
      cancelled = true;
    };
  }, [deepLinkedVariant]);

  const closePicker = () => {
    setPickerFor(null);
    if (deepLinkedVariant) {
      const next = new URLSearchParams(searchParams);
      next.delete("variant");
      setSearchParams(next, { replace: true });
    }
  };

  const onLinked = (variant: ShopifyVariant, result: ShopifyLinkResult) => {
    closePicker();
    setNotice(
      result.reevaluatedOrders > 0
        ? `Linked ${shopifyVariantLabel(variant)}. ${result.reevaluatedOrders} waiting ${
            result.reevaluatedOrders === 1 ? "order was" : "orders were"
          } checked again.`
        : `Linked ${shopifyVariantLabel(variant)}.`,
    );
    setReloadTick((n) => n + 1);
  };

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const result = await api.refreshShopifyProducts();
      setNotice(
        `Downloaded ${result.products} products (${result.variants} sizes)` +
          (result.removed > 0 ? `; ${result.removed} no longer in Shopify.` : ".") +
          (result.truncatedProducts.length > 0
            ? ` Some sizes of ${result.truncatedProducts.join(", ")} did not fit and are missing.`
            : ""),
      );
      setReloadTick((n) => n + 1);
    } catch (e) {
      setError(errText(e, "Could not refresh from Shopify"));
    } finally {
      setRefreshing(false);
    }
  };

  const unlink = async (variant: ShopifyVariant) => {
    if (!variant.linkedTo) return;
    setError(null);
    try {
      await api.removeShopifyLink(variant.linkedTo.linkId);
      setNotice(`Unlinked ${shopifyVariantLabel(variant)}.`);
      setReloadTick((n) => n + 1);
    } catch (e) {
      setError(errText(e, "Could not unlink"));
    }
  };

  return (
    <section className="page-stack">
      <div className="page-header">
        <div>
          <h2 className="page-title">Shopify products</h2>
          <p className="page-subtitle">
            Link each Shopify product size to a bouquet recipe or an item. Web orders reach the
            shops only once everything in them is linked.
          </p>
        </div>
      </div>

      <div className="card shopify-products-toolbar">
        <input
          className="shopify-products-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, SKU or barcode"
          aria-label="Search Shopify products"
        />
        <SegmentedControl
          ariaLabel="Show"
          options={[
            { value: "unlinked", label: "Not linked" },
            { value: "linked", label: "Linked" },
            { value: "all", label: "All" },
          ]}
          value={filter}
          onChange={setFilter}
        />
        <button type="button" className="button tinted" onClick={() => setMatching(true)}>
          Match by barcode
        </button>
        <button type="button" className="button" onClick={() => void refresh()} disabled={refreshing}>
          {refreshing ? "Downloading..." : "Refresh from Shopify"}
        </button>
        <span className="shopify-products-toolbar__meta">
          {refreshedAt ? `Updated ${new Date(refreshedAt).toLocaleString()}` : "Not downloaded yet"}
        </span>
      </div>

      {notice ? <div className="alert success">{notice}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}

      {variants === null ? (
        <p className="muted">Loading...</p>
      ) : variants.length === 0 ? (
        <div className="card">
          <p className="muted">
            {!refreshedAt
              ? "The product list has not been downloaded yet. Once the store is connected it downloads by itself, or press Refresh from Shopify."
              : appliedQuery
                ? "No Shopify product matches this search."
                : filter === "unlinked"
                  ? "Every Shopify product is linked."
                  : "No Shopify products here."}
          </p>
        </div>
      ) : (
        <div className="shopify-variant-grid">
          {variants.map((variant) => (
            <VariantCard
              key={variant.shopifyVariantId}
              variant={variant}
              onLink={() => setPickerFor(variant)}
              onUnlink={() => void unlink(variant)}
            />
          ))}
        </div>
      )}

      {pickerFor ? (
        <ShopifyLinkPicker
          variant={pickerFor}
          onClose={closePicker}
          onLinked={(result) => onLinked(pickerFor, result)}
        />
      ) : null}
      {matching ? (
        <ShopifyBarcodeMatchModal
          onClose={() => setMatching(false)}
          onDone={(message) => {
            setMatching(false);
            setNotice(message);
            setReloadTick((n) => n + 1);
          }}
        />
      ) : null}
    </section>
  );
}

function VariantCard({
  variant,
  onLink,
  onUnlink,
}: {
  variant: ShopifyVariant;
  onLink: () => void;
  onUnlink: () => void;
}) {
  const link = variant.linkedTo;
  return (
    <article className={variant.removed ? "shopify-variant-card is-removed" : "shopify-variant-card"}>
      <div className="shopify-variant-card__photo">
        {variant.imageUrl ? (
          <img src={variant.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />
        ) : (
          <span>No photo</span>
        )}
      </div>
      <div className="shopify-variant-card__body">
        <span className="shopify-variant-card__title">{variant.productTitle}</span>
        {variant.variantTitle ? (
          <span className="shopify-variant-card__meta">{variant.variantTitle}</span>
        ) : null}
        <span className="shopify-variant-card__meta">
          {money(variant.priceEur)}
          {variant.sku ? ` · SKU ${variant.sku}` : ""}
          {variant.barcode ? ` · ${variant.barcode}` : ""}
        </span>
        <div className="shopify-variant-card__badges">
          {variant.waitingOrders > 0 ? (
            <span className="badge warning">
              Waiting in {variant.waitingOrders} {variant.waitingOrders === 1 ? "order" : "orders"}
            </span>
          ) : null}
          {variant.removed ? <span className="badge danger">No longer in Shopify</span> : null}
          {!variant.removed && variant.status !== "ACTIVE" ? (
            <span className="badge muted">{variant.status.toLowerCase()}</span>
          ) : null}
        </div>
        <div className="shopify-variant-card__link">
          {link ? (
            <>
              {link.type === "ITEM" ? (
                <ItemThumb itemId={link.id} imageKey={link.imageKey} alt="" size={32} />
              ) : (
                <BouquetTemplateThumb templateId={link.id} imageKey={link.imageKey} alt="" size={32} />
              )}
              <span>
                {link.type === "ITEM" ? "Item: " : "Recipe: "}
                {link.name}
              </span>
            </>
          ) : (
            <span className="muted">Not linked</span>
          )}
        </div>
      </div>
      <div className="shopify-variant-card__actions">
        {variant.removed ? null : (
          <button type="button" className="button tinted" onClick={onLink}>
            {link ? "Change" : "Link"}
          </button>
        )}
        {link ? (
          <button type="button" className="button plain" onClick={onUnlink}>
            Unlink
          </button>
        ) : null}
      </div>
    </article>
  );
}
